import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, CopyButton } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { OneTimeSecret } from '../../components/OneTimeSecret';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * TOTP enrolment for a platform administrator.
 *
 * The page used to say enrolment was pending a workstream while the whole
 * flow (setup, enable, disable, recovery codes) had shipped and the tenant
 * console had been using it for months. This is the highest-privilege account
 * on the system, so it was the one account that could not be protected.
 *
 * Recovery codes go through `OneTimeSecret`: they are shown once, and someone
 * who needs them is by definition someone whose authenticator is gone, so a
 * stray Escape key costs them the account.
 */
export function TwoFactorPanel() {
  const t = useT();
  const { user, refresh } = useAuth();
  const enabled = user?.two_factor_enabled ?? false;

  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  const status = useQuery({
    queryKey: ['admin', '2fa', 'status'],
    queryFn: () => identity.twoFactorStatus(),
    retry: false
  });

  async function startSetup() {
    setBusy(true);
    try {
      const res = await identity.twoFactorSetup();
      if (!res.data.secret) {
        // The SDK documents `secret` as null when enrolment already happened;
        // without this branch the button simply stops spinning and says nothing.
        notifyError(t('settings.security.twoFactor.alreadySetUp'), t('settings.security.twoFactor.title'));
        await refresh();
        return;
      }
      setSecret(res.data.secret);
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.security.twoFactor.title'));
    } finally {
      setBusy(false);
    }
  }

  async function enable() {
    setBusy(true);
    try {
      const res = await identity.twoFactorEnable(code.trim());
      setRecoveryCodes(res.data.recovery_codes);
      setSecret(null);
      setCode('');
      await refresh();
      await status.refetch();
      notifySuccess(t('settings.security.twoFactor.toast.enabledBody'), t('settings.security.twoFactor.toast.enabled'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('settings.security.twoFactor.codeMismatch'), t('settings.security.twoFactor.title'));
    } finally {
      setBusy(false);
    }
  }

  /*
   * No confirmation dialog before disabling: the six digits from the
   * authenticator are the confirmation, and a TOTP code lives thirty seconds,
   * so a modal in the middle is what makes the attempt fail. The consequence
   * is stated beside the button instead.
   */
  async function disable() {
    setBusy(true);
    try {
      await identity.twoFactorDisable(code.trim());
      setCode('');
      await refresh();
      await status.refetch();
      notifySuccess(t('settings.security.twoFactor.toast.disabledBody'), t('settings.security.twoFactor.toast.disabled'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('settings.security.twoFactor.codeMismatch'), t('settings.security.twoFactor.title'));
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    try {
      const res = await identity.twoFactorRecoveryCodes(code.trim());
      setRecoveryCodes(res.data.recovery_codes);
      setCode('');
      await status.refetch();
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('settings.security.twoFactor.codeMismatch'), t('settings.security.twoFactor.title'));
    } finally {
      setBusy(false);
    }
  }

  const remaining = status.data?.data.recovery_codes_remaining;

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs">
            <Title order={5}>{t('settings.security.twoFactor.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.twoFactor.subtitle')}</Text>
          </Stack>
          <StatusBadge status={enabled ? 'healthy' : 'warning'}>
            {enabled ? t('common.enabled') : t('settings.security.twoFactor.notEnrolled')}
          </StatusBadge>
        </Group>

        {!enabled && (
          <Alert color="warning">{t('settings.security.twoFactor.unprotectedWarning')}</Alert>
        )}

        {!enabled && !secret && (
          <Stack gap="sm">
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.twoFactor.intro')}</Text>
            <Group gap="sm">
              <Button loading={busy} onClick={startSetup}>{t('settings.security.twoFactor.actions.setup')}</Button>
            </Group>
          </Stack>
        )}

        {!enabled && secret && (
          <Stack gap="md">
            <Text role="bodySmall">{t('settings.security.twoFactor.setup.step1')}</Text>
            {/*
              The admin console does not bundle a QR renderer, so the shared
              secret is typed into the authenticator. It stays copyable rather
              than being transcribed by eye.
            */}
            <Stack gap="xs">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.security.twoFactor.setup.orType')}</Text>
              <code style={{ wordBreak: 'break-all', userSelect: 'all' }}>{secret}</code>
              <CopyButton value={secret}>
                {({ copied, failed, copy }) => (
                  <Button size="xs" variant="subtle" onClick={copy} style={{ alignSelf: 'flex-start' }}>
                    {failed ? t('settings.secret.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                  </Button>
                )}
              </CopyButton>
            </Stack>
            <TextInput
              label={t('settings.security.twoFactor.code')}
              description={t('settings.security.twoFactor.setup.step2')}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              inputMode="numeric"
            />
            <Group gap="sm">
              <Button loading={busy} onClick={enable} disabled={!code.trim()}>{t('settings.security.twoFactor.actions.enable')}</Button>
              <Button variant="default" onClick={() => { setSecret(null); setCode(''); }}>{t('action.cancel')}</Button>
            </Group>
          </Stack>
        )}

        {enabled && (
          <Stack gap="md">
            {typeof remaining === 'number' && (
              <Text
                role="bodySmall"
                c={remaining <= 3 ? 'var(--rr-color-warning)' : 'var(--rr-color-text-muted)'}
              >
                {t('settings.security.twoFactor.codesRemaining', { count: remaining })}
              </Text>
            )}
            <TextInput
              label={t('settings.security.twoFactor.code')}
              description={t('settings.security.twoFactor.manageHint')}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              inputMode="numeric"
            />
            <Group gap="sm">
              <Button variant="default" loading={busy} onClick={regenerate} disabled={!code.trim()}>
                {t('settings.security.twoFactor.actions.newCodes')}
              </Button>
              <Button color="red" variant="subtle" loading={busy} onClick={disable} disabled={!code.trim()}>
                {t('settings.security.twoFactor.actions.disable')}
              </Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('settings.security.twoFactor.disableConsequence')}
            </Text>
          </Stack>
        )}

        <OneTimeSecret
          value={recoveryCodes ? recoveryCodes.join('\n') : null}
          lines={recoveryCodes ?? undefined}
          title={t('settings.security.twoFactor.codes.title')}
          intro={t('settings.security.twoFactor.codes.intro')}
          filename={`repletoryreach-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`}
          onAcknowledge={() => setRecoveryCodes(null)}
        />
      </Stack>
    </Card>
  );
}
